import { useState, useEffect, useCallback } from 'react';
import { Trophy, ArrowClockwise, CalendarBlank, CaretLeft, CaretRight, Television } from '@phosphor-icons/react';
import api from '@/lib/api';

function TournamentLeaderboard({ event }) {
  const top10 = (event.players || []).slice(0, 10);
  const isLive = event.state === 'in';
  const hasBroadcasts = event.broadcasts?.length > 0;
  return (
    <div>
      <p className="text-[11px] font-sans font-semibold text-foreground leading-tight">{event.tournament}</p>
      <div className="flex items-center gap-2 mt-0.5 mb-2">
        <span className="text-[9px] text-muted-foreground font-sans">{event.tour}</span>
        <span className="text-[9px] text-muted-foreground font-sans">·</span>
        <span className={`text-[9px] font-sans font-semibold ${isLive ? 'text-green-600' : event.state === 'post' ? 'text-muted-foreground' : 'text-blue-500'}`}>
          {event.status}
        </span>
      </div>
      {/* Broadcast chips */}
      {hasBroadcasts && (
        <div className="flex flex-wrap gap-1 mb-2">
          {event.broadcasts.slice(0, 3).map((bc, i) => (
            <span key={i} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-sans font-bold text-muted-foreground bg-muted/50 uppercase">
              <Television size={8} /> {bc}
            </span>
          ))}
        </div>
      )}
      {top10.length > 0 ? (
        <div className="space-y-0.5" data-testid="leaderboard-players">
          {top10.map((player, i) => (
            <div
              key={i}
              className={`flex items-center justify-between py-1.5 px-2 rounded-sm text-xs font-sans ${i < 3 ? 'bg-[#D4AF37]/5' : ''}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className={`w-5 text-right font-bold flex-shrink-0 ${i < 3 ? 'text-[#D4AF37]' : 'text-muted-foreground'}`}>
                  {player.position}
                </span>
                <span className="text-foreground font-medium truncate">{player.name}</span>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                {player.thru && <span className="text-muted-foreground text-[10px]">{player.thru}</span>}
                <span className={`font-bold min-w-[28px] text-right ${
                  player.score && player.score.startsWith('-') ? 'text-red-500' :
                  player.score === 'E' ? 'text-foreground' : 'text-muted-foreground'
                }`}>
                  {player.score || 'E'}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="py-4 text-center">
          <CalendarBlank size={20} className="mx-auto text-muted-foreground/40 mb-2" />
          <p className="text-xs text-muted-foreground font-sans">
            {new Date(event.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            {event.end_date && ` - ${new Date(event.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
          </p>
        </div>
      )}
    </div>
  );
}

export default function LeaderboardWidget() {
  const [tournaments, setTournaments] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);

  const fetchLeaderboard = useCallback(async () => {
    try {
      const { data } = await api.get('/leaderboard');
      setTournaments(data.tournaments || []);
      setUpdatedAt(data.updated_at);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchLeaderboard(); }, [fetchLeaderboard]);

  // Auto-rotate every 5 seconds if multiple tournaments
  useEffect(() => {
    if (tournaments.length <= 1) return;
    const timer = setInterval(() => {
      setActiveIdx(prev => (prev + 1) % tournaments.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [tournaments.length]);

  if (loading) return (
    <div className="p-4 animate-pulse">
      <div className="h-4 bg-muted rounded w-2/3 mb-3" />
      <div className="space-y-2">{[...Array(5)].map((_, i) => <div key={i} className="h-3 bg-muted rounded" />)}</div>
    </div>
  );

  if (!tournaments.length) return (
    <div className="p-4 text-center">
      <Trophy size={24} className="mx-auto text-muted-foreground/40" />
      <p className="text-xs text-muted-foreground font-sans mt-2">No tournaments available</p>
    </div>
  );

  const current = tournaments[activeIdx] || tournaments[0];

  return (
    <div className="p-4" data-testid="leaderboard-widget">
      {/* Header with nav arrows */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Trophy size={16} weight="fill" className="text-[#D4AF37]" />
          <h3 className="text-xs font-sans font-bold uppercase tracking-wider text-foreground">
            {current.state === 'in' ? 'Live' : current.state === 'post' ? 'Final' : 'Upcoming'}
          </h3>
        </div>
        <div className="flex items-center gap-1">
          {tournaments.length > 1 && (
            <>
              <button
                onClick={() => setActiveIdx(p => (p - 1 + tournaments.length) % tournaments.length)}
                className="text-muted-foreground hover:text-foreground transition-colors p-0.5"
                data-testid="leaderboard-prev"
              >
                <CaretLeft size={12} weight="bold" />
              </button>
              <span className="text-[9px] text-muted-foreground font-sans font-semibold min-w-[24px] text-center">
                {activeIdx + 1}/{tournaments.length}
              </span>
              <button
                onClick={() => setActiveIdx(p => (p + 1) % tournaments.length)}
                className="text-muted-foreground hover:text-foreground transition-colors p-0.5"
                data-testid="leaderboard-next"
              >
                <CaretRight size={12} weight="bold" />
              </button>
            </>
          )}
          <button onClick={fetchLeaderboard} className="text-muted-foreground hover:text-foreground transition-colors ml-1" data-testid="leaderboard-refresh">
            <ArrowClockwise size={14} />
          </button>
        </div>
      </div>

      {/* Dots indicator */}
      {tournaments.length > 1 && (
        <div className="flex justify-center gap-1 mb-3">
          {tournaments.map((_, i) => (
            <button
              key={i}
              onClick={() => setActiveIdx(i)}
              className={`w-1.5 h-1.5 rounded-full transition-colors ${i === activeIdx ? 'bg-[#D4AF37]' : 'bg-muted'}`}
            />
          ))}
        </div>
      )}

      <TournamentLeaderboard event={current} />

      {updatedAt && (
        <p className="text-[8px] text-muted-foreground font-sans mt-2 text-right">
          Updated {new Date(updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </p>
      )}
    </div>
  );
}
